import { Box, Button, Card, CardContent, Chip, CircularProgress, Typography } from "@mui/material";
import { FeeDuesComparisonChart } from "./FeeDuesComparisonChart";
import { cardTitleSx, dashboardCardSx, heroValueSx } from "../responsiveText";

export interface FeeDuesCardProps {
  isLoading: boolean;
  error: string | null;
  amountDue: number;
  status: "paid" | "pending";
  academicYear: string;
  currentYearTotal: number;
  previousYearTotal: number;
  previousYearLabel: string;
  onPayNow: () => void;
}

export const FeeDuesCard = ({
  isLoading,
  error,
  amountDue,
  status,
  academicYear,
  currentYearTotal,
  previousYearTotal,
  previousYearLabel,
  onPayNow,
}: FeeDuesCardProps) => {
  return (
    <Card elevation={0} sx={dashboardCardSx}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={cardTitleSx}>
              Fee Dues
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Academic Year {academicYear}
            </Typography>
          </Box>
          {!isLoading && !error && (
            <Chip
              label={status === "paid" ? "PAID" : "PENDING"}
              color={status === "paid" ? "success" : "warning"}
              variant="outlined"
              size="small"
              sx={{ height: 22, fontSize: 11, fontWeight: 700, letterSpacing: "0.04em" }}
            />
          )}
        </Box>

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!isLoading && error && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Couldn't load fee dues. Please try again later.
          </Typography>
        )}

        {!isLoading && !error && (
          <Box sx={{ display: "flex", gap: { xs: 2, sm: 4 }, alignItems: "center", flexWrap: "wrap", mt: 1.5 }}>
            <Box sx={{ minWidth: 200, width: { xs: "100%", sm: "auto" } }}>
              <Typography variant="h5" fontWeight={700} sx={heroValueSx}>
                ₹{amountDue.toLocaleString("en-IN")}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={onPayNow}
                  disabled={status === "paid"}
                  sx={{ width: { xs: "100%", sm: "auto" }, px: 3, py: 1, borderRadius: 2, textTransform: "none", fontWeight: 600, boxShadow: "none" }}
                >
                  Pay Now
                </Button>
              </Box>
            </Box>

            <Box sx={{ flex: 1, minWidth: 260, width: { xs: "100%", sm: "auto" } }}>
              <FeeDuesComparisonChart
                currentYearTotal={currentYearTotal}
                previousYearTotal={previousYearTotal}
                currentYearLabel={academicYear}
                previousYearLabel={previousYearLabel}
              />
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
